import React, { memo, useCallback, useMemo } from 'react';
import { pick } from 'ramda';
import { useLocation } from 'react-router-dom';
import { clsx } from 'js/utils/classNames.util';
import { useAppSelector } from 'js/store/hooks';
import { ReCaptcha } from 'js/components/main/ReCaptcha';
import { Label } from 'js/components/common/Fields/Label';
import { Radio } from 'js/components/common/Fields/Radio';
import { Input } from 'js/components/common/Fields/Input';
import { Select } from 'js/components/common/Fields/Select';
import { useComponentTranslations } from 'js/hooks/component';
import { Checkbox } from 'js/components/common/Fields/Checkbox';
import { DateInput } from 'js/components/common/Fields/DateInput';
import { FORM_FIELD_TYPE, DATE_FIELD_DISPLAY_TYPE } from 'js/components/common/Form/constants';
import { DatePicker, convertISOToPicker, convertPickerToISO } from 'js/components/common/Fields/DatePicker';

import * as hooks from './hooks';
import { Limits } from '../Limits';
import * as helpers from '../../helpers';
import { IBANField } from '../IBANField';
import { PhoneField } from '../PhoneField';
import { useGetFieldId } from '../../hooks';
import * as actions from '../../store/actions';
import { DepositLimits } from '../DepositLimits';
import { NationalIdField } from '../NationalIdField';
import { useDynamicReducerDispatch } from '../../store';
import { PlaceOfBirthField } from '../PlaceOfBirthField';
import { ContactPreferences } from '../ContactPreferences';
import { AddressLookupField } from '../AddressLookupField';
import { PlaceOfResidenceField } from '../PlaceOfResidenceField';
import { AddressLookupSwissField } from '../AddressLookupSwissField';

import style from './ChangeFormField.scss';

export const ChangeFormField = memo(({
    field,
    disabled,
    isFieldHidden,
    onChange,
    className: classNameProp
}) => {
    const { search } = useLocation();
    const translate = useComponentTranslations();
    const dispatch = useDynamicReducerDispatch();
    const getFieldId = useGetFieldId();
    const { allowedCurrencies, allowedCountries } = useAppSelector(pick(['allowedCurrencies', 'allowedCountries']));

    const className = useMemo(() => clsx([
        classNameProp,
        isFieldHidden && 'hidden',
        `cms-form-field-name-${field.name}`
    ]), [classNameProp, isFieldHidden, field.name]);

    const {
        isReCaptchaVisible,
        onReCaptchaTokenValidate,
        onReCaptchaTokenError,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    } = hooks.useRecaptchaValidation();

    const internalValidate = hooks.useInternalValidate({
        field,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    });

    const linkedPasswordFields = hooks.useLinkedPasswordFields(field.name);

    const {
        baseType,
        confirmationInfoTooltip,
        confirmationPlaceholder: fieldConfirmPlaceholder,
        customList = [],
        defaultValue,
        urlQueryParameter,
        displayType = DATE_FIELD_DISPLAY_TYPE.SELECTOR,
        enableEscapeSymbols,
        escapeSymbolsRegExp,
        hideFromUser,
        id,
        infoTooltip: fieldIntoTooltip,
        inputMode,
        name,
        placeholder: fieldPlaceholder,
        readOnly,
        requiresConfirmation,
        selectedList,
        type,
        validationRules
    } = field;
    const fieldId = getFieldId(id);
    const [label, getLabelText] = hooks.useFieldLabel(field.label);
    const [confirmationLabel, getConfirmationLabelText] = hooks.useFieldLabel(field.confirmationLabel);
    const placeholder = fieldPlaceholder && translate(fieldPlaceholder, fieldPlaceholder);
    const infoTooltip = fieldIntoTooltip && translate(fieldIntoTooltip, fieldIntoTooltip);
    const confirmationPlaceholder = fieldConfirmPlaceholder && translate(fieldConfirmPlaceholder, fieldConfirmPlaceholder);

    const {
        isLabelAsteriskEnabled,
        isPlaceholderAsteriskEnabled
    } = field.validation && validationRules?.find(rule => rule.type === 'required') || {};

    const countriesList = useMemo(() => helpers.getCountriesList(allowedCountries), [allowedCountries]);

    const currenciesList = useMemo(() => helpers.getCurrenciesList(allowedCurrencies), [allowedCurrencies]);

    const preventInput = useCallback((event) => event.preventDefault(), []);

    if (hideFromUser) {
        const params = new URLSearchParams(search);

        return <input
            type="hidden"
            name={name}
            value={params.get(urlQueryParameter) || defaultValue}
        />;
    }

    switch (type) {
        case FORM_FIELD_TYPE.TEXT_LABEL:
            return <div className={`${style.textLabel} ${className}`} id={fieldId}>{label}</div>;
        case FORM_FIELD_TYPE.EMAIL:
        case FORM_FIELD_TYPE.USER_NAME:
            return (
                <>
                    <Input
                        id={fieldId}
                        name={name}
                        label={label}
                        disabled={disabled}
                        onChange={onChange}
                        className={className}
                        placeholder={placeholder}
                        infoTooltip={infoTooltip}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        type={type === FORM_FIELD_TYPE.EMAIL ? FORM_FIELD_TYPE.EMAIL : baseType}
                        aria-label={getLabelText() || placeholder || translate('playtech.mobile.system.input.aria-label')}
                        {...readOnly && { readOnly: true }}
                        {...inputMode && { inputMode }}
                        {...internalValidate && {
                            validate: internalValidate,
                            validateFields: linkedPasswordFields,
                            // to rerender field with a new validateFields, it's needed to update another prop dependent from new validateFields
                            data: `${fieldId}_${linkedPasswordFields.join('-')}`
                        }}
                    />
                    {requiresConfirmation && (
                        <Input
                            aria-label={getConfirmationLabelText() || confirmationPlaceholder || translate('playtech.mobile.system.input.aria-label')}
                            className={className}
                            disabled={disabled}
                            id={`${fieldId}_confirmation`}
                            infoTooltip={confirmationInfoTooltip && translate(confirmationInfoTooltip, confirmationInfoTooltip)}
                            isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                            isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                            label={confirmationLabel}
                            name={`${name}_confirmation`}
                            onContextMenu={preventInput}
                            onCopy={preventInput}
                            onCut={preventInput}
                            onDrop={preventInput}
                            onPaste={preventInput}
                            placeholder={confirmationPlaceholder}
                            type={type}
                        />
                    )}
                    {isReCaptchaVisible && !isFieldHidden ? (
                        <div className={style.captcha}>
                            <ReCaptcha
                                operation="checkUsernameAvailability"
                                onValidate={onReCaptchaTokenValidate}
                                onError={onReCaptchaTokenError}
                            />
                        </div>
                    ) : null}
                </>
            );
        case FORM_FIELD_TYPE.TEXT:
        case FORM_FIELD_TYPE.PASSWORD:
            return <>
                <Input
                    id={fieldId}
                    type={type}
                    name={name}
                    label={label}
                    disabled={disabled}
                    onChange={onChange}
                    className={className}
                    placeholder={placeholder}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    aria-label={getLabelText() || placeholder || translate('playtech.mobile.system.input.aria-label')}
                    {...readOnly && { readOnly: true }}
                    {...inputMode && { inputMode }}
                    {...enableEscapeSymbols && { escapeSymbolsRegExp }}
                    {...type === FORM_FIELD_TYPE.PASSWORD && { validate: internalValidate }}
                />
                {requiresConfirmation && (
                    <Input
                        aria-label={getConfirmationLabelText() || confirmationPlaceholder || translate('playtech.mobile.system.input.aria-label')}
                        className={className}
                        disabled={disabled}
                        id={`${fieldId}_confirmation`}
                        infoTooltip={confirmationInfoTooltip && translate(confirmationInfoTooltip, confirmationInfoTooltip)}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        label={confirmationLabel}
                        name={`${name}_confirmation`}
                        onContextMenu={preventInput}
                        onCopy={preventInput}
                        onCut={preventInput}
                        onDrop={preventInput}
                        onPaste={preventInput}
                        placeholder={confirmationPlaceholder}
                        type={type}
                    />
                )}
            </>;
        case FORM_FIELD_TYPE.ADDRESS:
        case FORM_FIELD_TYPE.ADDRESS_2:
        case FORM_FIELD_TYPE.BUILDING_NUMBER:
        case FORM_FIELD_TYPE.HOUSE_NUMBER:
        case FORM_FIELD_TYPE.CITY:
        case FORM_FIELD_TYPE.PROPERTY_NUMBER:
        case FORM_FIELD_TYPE.PROVINCE:
        case FORM_FIELD_TYPE.STATE:
        case FORM_FIELD_TYPE.STREET_NAME:
        case FORM_FIELD_TYPE.SUBBUILDING:
        case FORM_FIELD_TYPE.UNIT_NUMBER:
        case FORM_FIELD_TYPE.ZIP:
            return (
                <Input
                    id={fieldId}
                    name={name}
                    label={label}
                    disabled={disabled}
                    onChange={onChange}
                    className={className}
                    placeholder={placeholder}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    aria-label={getLabelText() || placeholder || translate('playtech.mobile.system.input.aria-label')}
                    {...readOnly && { readOnly: true }}
                    {...inputMode && { inputMode }}
                />
        );

        case FORM_FIELD_TYPE.IBAN:
            return (
                <IBANField
                    name={name}
                    id={fieldId}
                    label={label}
                    disabled={disabled}
                    className={className}
                    placeholder={placeholder}
                    infoTooltip={infoTooltip}
                    validate={internalValidate}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    ariaLabel={getLabelText() || placeholder || translate('playtech.mobile.system.input.aria-label')}
                    {...readOnly && { readOnly: true }}
                />
            );
        case FORM_FIELD_TYPE.ADDRESS_LOOKUP:
            return (
                <AddressLookupField
                    field={field}
                    fieldId={fieldId}
                    disabled={disabled}
                    className={classNameProp}
                    validate={internalValidate}
                    classNames={{ autocomplete: className }}
                    isFieldHidden={isFieldHidden}
                    ChangeFormField={ChangeFormField}
                />
            );
        case FORM_FIELD_TYPE.ADDRESS_LOOKUP_SWISS:
            return (
                <AddressLookupSwissField
                    field={field}
                    fieldId={fieldId}
                    disabled={disabled}
                    className={classNameProp}
                    validate={internalValidate}
                    classNames={{ autocomplete: className }}
                    isFieldHidden={isFieldHidden}
                    ChangeFormField={ChangeFormField}
                />
            );
        case FORM_FIELD_TYPE.PLACE_OF_BIRTH:
            return (
                <PlaceOfBirthField
                    field={field}
                    disabled={disabled}
                    className={classNameProp}
                    isFieldHidden={isFieldHidden}
                    ChangeFormField={ChangeFormField}
                />
            );
        case FORM_FIELD_TYPE.PLACE_OF_RESIDENCE:
            return (
                <PlaceOfResidenceField
                    field={field}
                    disabled={disabled}
                    className={classNameProp}
                    isFieldHidden={isFieldHidden}
                    ChangeFormField={ChangeFormField}
                />
            );
        case FORM_FIELD_TYPE.ITALIAN_ID:
        case FORM_FIELD_TYPE.PORTUGAL_ID:
        case FORM_FIELD_TYPE.POLAND_ID:
        case FORM_FIELD_TYPE.ROMANIAN_ID:
        case FORM_FIELD_TYPE.SOUTH_AFRICAN_ID:
        case FORM_FIELD_TYPE.SPANISH_ID:
            return (
                <NationalIdField
                    id={fieldId}
                    type={type}
                    name={name}
                    label={label}
                    disabled={disabled}
                    className={className}
                    placeholder={placeholder}
                    infoTooltip={infoTooltip}
                    validate={internalValidate}
                    isFieldHidden={isFieldHidden}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    ariaLabel={getLabelText() || placeholder || translate('playtech.mobile.system.input.aria-label')}
                    {...readOnly && { readOnly: true }}
                    {...inputMode && { inputMode }}
                />
            );

        case FORM_FIELD_TYPE.CHECKBOX:
            return (
                <Checkbox
                    name={name}
                    id={fieldId}
                    label={label}
                    disabled={disabled}
                    className={className}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    aria-label={getLabelText() || translate('playtech.mobile.system.checkbox.aria-label')}
                    {...readOnly && { readOnly: true }}
                />
            );

        case FORM_FIELD_TYPE.RADIO_BUTTON:
            return <>
                {(label || isLabelAsteriskEnabled) && (
                    <Label
                        id={fieldId}
                        label={label}
                        infoTooltip={infoTooltip}
                        className={isFieldHidden ? 'hidden' : ''}
                        classNames={{ label: style.radioGroupLabel }}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    />
                )}
                {customList.map((item) => (
                    <Radio
                        name={name}
                        key={item.id}
                        value={item.value}
                        disabled={disabled}
                        className={className}
                        label={item.key && translate(item.key, item.key)}
                        aria-label={translate([item.key, 'playtech.mobile.system.radio.aria-label'])}
                        {...readOnly && { readOnly: true }}
                    />
                ))}
            </>;

        case FORM_FIELD_TYPE.COUNTRY:
        case FORM_FIELD_TYPE.CURRENCY_CODE:
        case FORM_FIELD_TYPE.DROPDOWN:
        case FORM_FIELD_TYPE.BIRTH_PROVINCE:
        case FORM_FIELD_TYPE.CITY_OF_BIRTH:
        case FORM_FIELD_TYPE.COUNTRY_OF_BIRTH:
        case FORM_FIELD_TYPE.COUNTRY_OF_RESIDENCE:
        case FORM_FIELD_TYPE.CITY_OF_RESIDENCE:
        case FORM_FIELD_TYPE.PROVINCE_OF_RESIDENCE: {
            const selectedArray = (() => {
                switch (selectedList) {
                    case 'countries': return countriesList;
                    case 'currencies': return currenciesList;
                    default:
                        return customList.map(({
                            key,
                            value
                        }) => ({
                            label: translate(key, key),
                            value
                        }));
                }
            })();

            return (
                <Select
                    id={fieldId}
                    type={type}
                    name={name}
                    label={label}
                    disabled={disabled}
                    className={className}
                    options={selectedArray}
                    placeholder={placeholder}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    aria-label={getLabelText() || placeholder || translate('playtech.mobile.system.select.aria-label')}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    {...readOnly && { readOnly: true }}
                    onChange={(event) => {
                        if (type === FORM_FIELD_TYPE.CURRENCY_CODE) {
                            dispatch(actions.setLimitsCurrency(event.target.value));
                        }

                        onChange?.(event);
                    }}
                />
            );
        }

        case FORM_FIELD_TYPE.DATE_PICKER:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH_INNER: {
            return displayType === DATE_FIELD_DISPLAY_TYPE.SELECTOR
                ? (
                    <DatePicker
                        name={name}
                        label={label}
                        disabled={disabled}
                        onChange={onChange}
                        className={className}
                        infoTooltip={infoTooltip}
                        parse={convertPickerToISO}
                        format={convertISOToPicker}
                        range={helpers.getRangeFromValidation(field)}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        {...readOnly && { readOnly: true }}
                    />
                ) : (
                    <DateInput
                        name={name}
                        id={fieldId}
                        label={label}
                        disabled={disabled}
                        onChange={onChange}
                        className={className}
                        infoTooltip={infoTooltip}
                        aria-label={getLabelText()}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        {...readOnly && { readOnly: true }}
                    />
                );
        }

        case FORM_FIELD_TYPE.CELLPHONE:
        case FORM_FIELD_TYPE.PHONE:
            return (
                <PhoneField
                    field={field}
                    fieldId={fieldId}
                    disabled={disabled}
                    className={className}
                    validate={internalValidate}
                    isFieldHidden={isFieldHidden}
                    classNames={{ captcha: style.captcha }}
                    isReCaptchaVisible={isReCaptchaVisible}
                    linkedPasswordFields={linkedPasswordFields}
                    onReCaptchaTokenError={onReCaptchaTokenError}
                    onReCaptchaTokenValidate={onReCaptchaTokenValidate}
                />
            );

        case FORM_FIELD_TYPE.DEPOSIT_LIMITS: {
            return (
                <DepositLimits
                    field={field}
                    fieldId={fieldId}
                    className={className}
                />
            );
        }
        case FORM_FIELD_TYPE.BET_LIMITS:
        case FORM_FIELD_TYPE.LOSS_LIMITS: {
            return (
                <Limits
                    field={field}
                    fieldId={fieldId}
                    className={className}
                    type={field.type === FORM_FIELD_TYPE.BET_LIMITS ? 'bet' : 'loss'}
                />
            );
        }

        case FORM_FIELD_TYPE.CONTACT_PREFERENCES: {
            return (
                <ContactPreferences
                    className={className}
                    field={field}
                    fieldId={fieldId}
                />
            );
        }

        default:
            return null;
    }
});
